import PropTypes from 'prop-types'
import { Alert, Box, Typography } from '@mui/material'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import createDialogTitle from '../../../generic/utilities/dialogUtil'
import { DOCUMENT_SCOPES } from '../../../generic/qr/qrDocumentResources'
import { contextMismatch } from '../../../generic/qr/qrEntityTypes'
import { EYEBROW_SX } from '../../../generic/qr/qrReadingStyles'
import MosSmartDialog from '../common/MosSmartDialog'
import { QR_CONTEXT_MATCHES } from './qrLookupApi'
import QrDocumentsPanel from './QrDocumentsPanel'
import QrLookupHeaderBlock from './QrLookupHeaderBlock'

/**
 * What the operator sees after a label resolves: the element's sheet and its documents, opened on the
 * level the station asked for.
 *
 * Two columns, because the two answers do not compete for room that way. The sheet is as long as the
 * entity type needs — two fields for an order, six for a fabrication order — and the drawings keep the
 * full height of the dialog whatever that number is. Stacked, every extra field Sun decides to send
 * would push the drawing further down, which is the one thing this dialog exists to show.
 *
 * One shape, because there is one entry point. The widget used to have a second one that reached this
 * dialog with the field grid hidden and the sources filtered to the reference; it was the same
 * documents with less around them, so the dialog no longer has modes.
 *
 * A foreign element is shown, not blocked. Reading documentation is never destructive, and refusing
 * to open it would stop an operator from checking any reference other than the one being worked on —
 * so the mismatch is a warning the operator cannot miss, and Sun has already recorded it.
 */

// Wide enough for a label code in monospace plus its longest field label, and no wider: past that the
// sheet starts taking room from the drawings for nothing.
const SHEET_WIDTH = '22rem'

const QrLookupResultDialog = ({
  open,
  result,
  preferredScope = DOCUMENT_SCOPES.REFERENCE,
  activeFabricationOrderCode = '',
  onClose
}) => {
  const { t_ } = useSmartTranslation()

  if (!result) return null

  const isForeign = result.contextMatch === QR_CONTEXT_MATCHES.FOREIGN

  return (
    <MosSmartDialog
      setOpen={open}
      accept={false}
      cancel={false}
      close
      closeCallback={onClose}
      paperWidth='min(80rem, 96vw)'
      paperHeight='min(48rem, 92vh)'
      growContainerHeight
      title={createDialogTitle({
        color: isForeign ? 'warning' : 'primary',
        title: t_('Element detail')
      })}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0, width: '100%', gap: 1.5 }}>
        {/* Above both columns: the verdict is about the read, not about either half of it. */}
        {isForeign && (
          <Alert severity='warning' sx={{ textAlign: 'left', flexShrink: 0 }}>
            {mismatchWarning(t_, result.entityType, activeFabricationOrderCode)}
          </Alert>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 2, md: 3 },
            flexGrow: 1,
            minHeight: 0
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', md: SHEET_WIDTH },
              flexShrink: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              // A sheet longer than the dialog scrolls on its own instead of shortening the viewer.
              overflowY: { md: 'auto' }
            }}
          >
            <QrLookupHeaderBlock
              entityType={result.entityType}
              title={result.title}
              subtitle={result.subtitle}
              header={result.header}
              isForeign={isForeign}
            />

            {/* The audit row this read wrote, right under the sheet it belongs to rather than pinned to
                the bottom of a column that is taller than it. Not information for the shop floor: it is
                what support asks for when an operator reports having seen the wrong thing. */}
            {result.traceId > 0 && (
              <Typography variant='caption' color='text.secondary' sx={{ flexShrink: 0 }}>
                {t_('Trace') + ': ' + result.traceId}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              minWidth: 0,
              minHeight: 0,
              gap: 0.5,
              // The rule says what the two halves are; on one column the gap says it instead.
              borderLeft: { md: '1px solid' },
              borderColor: { md: 'divider' },
              pl: { md: 3 }
            }}
          >
            <Typography sx={{ ...EYEBROW_SX, flexShrink: 0, textAlign: 'left' }}>{t_('Documents')}</Typography>
            <QrDocumentsPanel
              sources={result.documentSources}
              orderLines={result.orderLines ?? []}
              preferredScope={preferredScope}
            />
          </Box>
        </Box>
      </Box>
    </MosSmartDialog>
  )
}

// The station's order is named only when the verdict was answered against it — for a lot it was
// answered against the machine warehouse, and the Location field already says which machine.
const mismatchWarning = (t_, entityType, activeFabricationOrderCode) => {
  const { key, namesFabricationOrder } = contextMismatch(entityType)
  const message = t_(key)
  if (!namesFabricationOrder || activeFabricationOrderCode === '') return message
  return message + ' (' + activeFabricationOrderCode + ')'
}

QrLookupResultDialog.propTypes = {
  open: PropTypes.bool,
  /** The normalised result of qrLookupApi.scanQrCode. */
  result: PropTypes.shape({
    entityType: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    header: PropTypes.array,
    documentSources: PropTypes.array,
    orderLines: PropTypes.array,
    contextMatch: PropTypes.string,
    traceId: PropTypes.number
  }),
  /** Which document level opens first when the element has it. */
  preferredScope: PropTypes.string,
  /** Shown inside the mismatch warning so the operator knows what the station IS working on. */
  activeFabricationOrderCode: PropTypes.string,
  onClose: PropTypes.func.isRequired
}

export default QrLookupResultDialog
